import React, { useState, useEffect, useContext, useCallback } from "react";
import { AppContext, apiClient } from "../App";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Activity, RefreshCw, ArrowLeft, TrendingUp, TrendingDown,
  ChevronDown, ChevronUp, Settings
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { useNavigate } from "react-router-dom";

// Available underlyings for option chain
const UNDERLYINGS = [
  { value: 'NIFTY', label: 'NIFTY 50', color: 'text-blue-400' },
  { value: 'BANKNIFTY', label: 'BANK NIFTY', color: 'text-purple-400' },
  { value: 'FINNIFTY', label: 'FIN NIFTY', color: 'text-cyan-400' },
  { value: 'MIDCPNIFTY', label: 'MIDCAP NIFTY', color: 'text-amber-400' },
  { value: 'SENSEX', label: 'SENSEX', color: 'text-green-400' },
  { value: 'BANKEX', label: 'BANKEX', color: 'text-pink-400' },
];

// Option Chain Row Component
const OptionRow = ({ strike, ce, pe, atmStrike, ltp }) => {
  const isATM = strike === atmStrike;
  const isITM_CE = strike < ltp;
  const isITM_PE = strike > ltp;
  
  // Map Dhan API fields to our display fields
  const ceData = ce ? {
    oi: ce.oi || 0,
    chg_oi: (ce.oi || 0) - (ce.previous_oi || 0),
    volume: ce.volume || 0,
    ltp: ce.last_price || 0,
    close: ce.previous_close_price || 0
  } : null;
  
  const peData = pe ? {
    oi: pe.oi || 0,
    chg_oi: (pe.oi || 0) - (pe.previous_oi || 0),
    volume: pe.volume || 0,
    ltp: pe.last_price || 0,
    close: pe.previous_close_price || 0
  } : null;
  
  return (
    <div className={cn(
      "grid grid-cols-9 gap-1 text-xs border-b border-[#1a1a1a] hover:bg-[#111]",
      isATM && "bg-amber-500/10 border-amber-500/30"
    )}>
      {/* CE Side */}
      <div className={cn(
        "p-2 text-right font-mono",
        isITM_CE ? "bg-green-900/20" : ""
      )}>
        <span className={ceData?.oi > 0 ? "text-green-400" : "text-[#555]"}>
          {ceData?.oi?.toLocaleString() || '-'}
        </span>
      </div>
      <div className={cn(
        "p-2 text-right font-mono",
        ceData?.chg_oi > 0 ? "text-green-400" : ceData?.chg_oi < 0 ? "text-red-400" : "text-[#555]"
      )}>
        {ceData?.chg_oi ? (ceData.chg_oi > 0 ? '+' : '') + ceData.chg_oi.toLocaleString() : '-'}
      </div>
      <div className="p-2 text-right font-mono text-[#888]">
        {ceData?.volume?.toLocaleString() || '-'}
      </div>
      <div className={cn(
        "p-2 text-right font-mono font-medium",
        ceData?.ltp > ceData?.close ? "text-green-400" : ceData?.ltp < ceData?.close ? "text-red-400" : "text-[#888]"
      )}>
        {ceData?.ltp > 0 ? ceData.ltp.toFixed(2) : '-'}
      </div>
      
      {/* Strike Price */}
      <div className={cn(
        "p-2 text-center font-bold",
        isATM ? "text-amber-400 bg-amber-500/20" : "text-white"
      )}>
        {strike}
        {isATM && <span className="ml-1 text-[9px] text-amber-400">ATM</span>}
      </div>
      
      {/* PE Side */}
      <div className={cn(
        "p-2 text-left font-mono font-medium",
        peData?.ltp > peData?.close ? "text-green-400" : peData?.ltp < peData?.close ? "text-red-400" : "text-[#888]"
      )}>
        {peData?.ltp > 0 ? peData.ltp.toFixed(2) : '-'}
      </div>
      <div className="p-2 text-left font-mono text-[#888]">
        {peData?.volume?.toLocaleString() || '-'}
      </div>
      <div className={cn(
        "p-2 text-left font-mono",
        peData?.chg_oi > 0 ? "text-green-400" : peData?.chg_oi < 0 ? "text-red-400" : "text-[#555]"
      )}>
        {peData?.chg_oi ? (peData.chg_oi > 0 ? '+' : '') + peData.chg_oi.toLocaleString() : '-'}
      </div>
      <div className={cn(
        "p-2 text-left font-mono",
        isITM_PE ? "bg-red-900/20" : ""
      )}>
        <span className={peData?.oi > 0 ? "text-red-400" : "text-[#555]"}>
          {peData?.oi?.toLocaleString() || '-'}
        </span>
      </div>
    </div>
  );
};

// Summary Stats Component
const SummaryStats = ({ chainData, underlying }) => {
  if (!chainData) return null;
  
  const { total_ce_oi, total_pe_oi, pcr, max_pain } = calculateStats(chainData);
  
  return (
    <div className="grid grid-cols-4 gap-2 p-3 bg-[#0a0a0a] rounded-lg border border-[#222]">
      <div className="text-center">
        <p className="text-[10px] text-[#666] uppercase">Total CE OI</p>
        <p className="text-sm font-bold text-green-400">{(total_ce_oi / 100000).toFixed(2)}L</p>
      </div>
      <div className="text-center">
        <p className="text-[10px] text-[#666] uppercase">Total PE OI</p>
        <p className="text-sm font-bold text-red-400">{(total_pe_oi / 100000).toFixed(2)}L</p>
      </div>
      <div className="text-center">
        <p className="text-[10px] text-[#666] uppercase">PCR</p>
        <p className={cn(
          "text-sm font-bold",
          pcr > 1 ? "text-green-400" : pcr < 0.8 ? "text-red-400" : "text-amber-400"
        )}>
          {pcr.toFixed(2)}
        </p>
      </div>
      <div className="text-center">
        <p className="text-[10px] text-[#666] uppercase">Max Pain</p>
        <p className="text-sm font-bold text-cyan-400">{max_pain}</p>
      </div>
    </div>
  );
};

// Calculate stats from chain data
const calculateStats = (chainData) => {
  let total_ce_oi = 0;
  let total_pe_oi = 0;
  let maxPainData = {};
  
  Object.entries(chainData || {}).forEach(([strike, data]) => {
    const strikeNum = parseFloat(strike);
    const ce_oi = data?.ce?.oi || 0;
    const pe_oi = data?.pe?.oi || 0;
    
    total_ce_oi += ce_oi;
    total_pe_oi += pe_oi;
    
    // Simple max pain calculation
    maxPainData[strike] = ce_oi + pe_oi;
  });
  
  // Find strike with max OI for max pain approximation
  let max_pain = '-';
  let maxOI = 0;
  Object.entries(maxPainData).forEach(([strike, oi]) => {
    if (oi > maxOI) {
      maxOI = oi;
      max_pain = parseFloat(strike).toFixed(0);
    }
  });
  
  const pcr = total_ce_oi > 0 ? total_pe_oi / total_ce_oi : 0;
  
  return { total_ce_oi, total_pe_oi, pcr, max_pain };
};

// Main Option Chain Component
export default function OptionChain() {
  const { connectedBroker, currentUser, hasPremium } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [underlying, setUnderlying] = useState('NIFTY');
  const [expiries, setExpiries] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState('');
  const [chainData, setChainData] = useState(null);
  const [lastPrice, setLastPrice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [strikeRange, setStrikeRange] = useState(10); // Number of strikes above/below ATM
  
  // Check if Dhan is connected
  const isDhanConnected = connectedBroker === 'dhan';
  
  // Load expiries when underlying changes
  useEffect(() => {
    if (!isDhanConnected) return;
    
    const loadExpiries = async () => {
      try {
        const response = await apiClient.get(`/dom/option-chain/expiries/${underlying}`);
        if (response.data?.success) {
          setExpiries(response.data.expiries || []);
          if (response.data.expiries?.length > 0) {
            setSelectedExpiry(response.data.expiries[0]);
          }
        }
      } catch (error) {
        console.error('Failed to load expiries:', error);
        toast.error('Failed to load expiry dates');
      }
    };
    
    loadExpiries();
  }, [underlying, isDhanConnected]);
  
  // Load option chain data
  const loadChainData = useCallback(async () => {
    if (!isDhanConnected || !selectedExpiry) return;
    
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/dom/option-chain/${underlying}/${selectedExpiry}`);
      if (response.data?.success) {
        setChainData(response.data.option_chain);
        setLastPrice(response.data.last_price);
      }
    } catch (error) {
      console.error('Failed to load option chain:', error);
      toast.error('Failed to load option chain data');
    } finally {
      setIsLoading(false);
    }
  }, [underlying, selectedExpiry, isDhanConnected]);
  
  // Load chain data when expiry changes
  useEffect(() => {
    if (selectedExpiry) {
      loadChainData();
    }
  }, [selectedExpiry, loadChainData]);
  
  // Auto refresh
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(loadChainData, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadChainData]);
  
  // Get sorted strikes around ATM
  const getDisplayStrikes = () => {
    if (!chainData || !lastPrice) return [];
    
    const strikes = Object.keys(chainData)
      .map(s => parseFloat(s))
      .sort((a, b) => a - b);
    
    // Find ATM strike
    const atmIndex = strikes.findIndex((s, i) => {
      const next = strikes[i + 1];
      return s <= lastPrice && (!next || next > lastPrice);
    });
    
    const startIndex = Math.max(0, atmIndex - strikeRange);
    const endIndex = Math.min(strikes.length, atmIndex + strikeRange + 1);
    
    return strikes.slice(startIndex, endIndex);
  };
  
  const displayStrikes = getDisplayStrikes();
  const atmStrike = displayStrikes.find(s => s <= lastPrice && displayStrikes.find(n => n > lastPrice) > s) || displayStrikes[Math.floor(displayStrikes.length / 2)];
  
  // Option Chain was only available with Dhan - show coming soon message
  return (
    <div className="h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="text-center p-8 bg-[#111] rounded-xl border border-[#222] max-w-md">
        <Activity className="w-12 h-12 text-purple-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Option Chain - Coming Soon</h2>
        <p className="text-[#888] text-sm mb-4">
          Option Chain with real-time Greeks and OI data is currently being developed.
          This feature will be available in a future update.
        </p>
        <Button onClick={() => navigate('/')} className="bg-orange-600 hover:bg-orange-700">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go to Dashboard
        </Button>
      </div>
    </div>
  );
  
  return (
    <div className="h-screen bg-[#0a0a0a] flex flex-col" data-testid="option-chain-page">
      {/* Header */}
      <header className="border-b border-[#222] bg-[#111] px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-400" />
              <h1 className="text-lg font-bold text-white">Option Chain</h1>
              <span className="px-2 py-0.5 bg-purple-900/30 text-purple-400 text-xs rounded">
                Dhan 20-Lvl
              </span>
            </div>
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-3">
            {/* Underlying */}
            <Select value={underlying} onValueChange={setUnderlying}>
              <SelectTrigger className="w-40 bg-[#0a0a0a] border-[#333] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#333]">
                {UNDERLYINGS.map(u => (
                  <SelectItem key={u.value} value={u.value}>
                    <span className={u.color}>{u.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Expiry */}
            <Select value={selectedExpiry} onValueChange={setSelectedExpiry}>
              <SelectTrigger className="w-36 bg-[#0a0a0a] border-[#333] text-white">
                <SelectValue placeholder="Expiry" />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#333]">
                {expiries.map(exp => (
                  <SelectItem key={exp} value={exp}>
                    {exp}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Strike Range */}
            <Select value={strikeRange.toString()} onValueChange={(v) => setStrikeRange(parseInt(v))}>
              <SelectTrigger className="w-28 bg-[#0a0a0a] border-[#333] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#333]">
                <SelectItem value="5">5 Strikes</SelectItem>
                <SelectItem value="10">10 Strikes</SelectItem>
                <SelectItem value="15">15 Strikes</SelectItem>
                <SelectItem value="20">20 Strikes</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Refresh */}
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? "bg-green-600" : ""}
            >
              <RefreshCw className={cn("w-4 h-4 mr-1", autoRefresh && "animate-spin")} />
              {autoRefresh ? "Auto" : "Manual"}
            </Button>
            
            <Button variant="outline" size="sm" onClick={loadChainData} disabled={isLoading}>
              <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
            </Button>
          </div>
        </div>
        
        {/* LTP Display */}
        {lastPrice && (
          <div className="mt-2 flex items-center gap-4">
            <span className="text-[#888]">{underlying} Spot:</span>
            <span className="text-xl font-bold text-white">{lastPrice?.toLocaleString()}</span>
          </div>
        )}
      </header>
      
      {/* Summary Stats */}
      <div className="px-4 py-3 border-b border-[#222]">
        <SummaryStats chainData={chainData} underlying={underlying} />
      </div>
      
      {/* Option Chain Table */}
      <div className="flex-1 overflow-auto">
        {/* Table Header */}
        <div className="sticky top-0 bg-[#111] border-b border-[#333] z-10">
          <div className="grid grid-cols-9 gap-1 text-[10px] uppercase text-[#666] font-semibold">
            <div className="p-2 text-right bg-green-900/20">CE OI</div>
            <div className="p-2 text-right bg-green-900/20">CHG OI</div>
            <div className="p-2 text-right bg-green-900/20">VOLUME</div>
            <div className="p-2 text-right bg-green-900/20">CE LTP</div>
            <div className="p-2 text-center bg-[#1a1a1a]">STRIKE</div>
            <div className="p-2 text-left bg-red-900/20">PE LTP</div>
            <div className="p-2 text-left bg-red-900/20">VOLUME</div>
            <div className="p-2 text-left bg-red-900/20">CHG OI</div>
            <div className="p-2 text-left bg-red-900/20">PE OI</div>
          </div>
        </div>
        
        {/* Table Body */}
        <div className="divide-y divide-[#1a1a1a]">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
            </div>
          ) : displayStrikes.length > 0 ? (
            displayStrikes.map(strike => (
              <OptionRow
                key={strike}
                strike={strike}
                ce={chainData?.[strike]?.ce}
                pe={chainData?.[strike]?.pe}
                atmStrike={atmStrike}
                ltp={lastPrice}
              />
            ))
          ) : (
            <div className="text-center py-20 text-[#666]">
              <p>No option chain data available</p>
              <p className="text-sm mt-1">Select an underlying and expiry to view</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
